import ExcelResultTable from "./ExcelResultTable";
import CalculationWarning from "./CalculationWarning";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    Paper,
    Typography,
    Backdrop,
    CircularProgress,
    Tabs,
    Tab,
    Skeleton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

const TorisphericalVesselCalculation = ({ data, loading, error }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";

    const [activeView, setActiveView] = useState(0);
    const [detailLoading, setDetailLoading] = useState(false);

    useEffect(() => {
        setDetailLoading(true);
        const timer = setTimeout(() => setDetailLoading(false), 400);
        return () => clearTimeout(timer);
    }, [activeView]);


    const isTower = activeView === 1;

    const renderSkeleton = () => (
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
            <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 22%" }, minWidth: 0 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
            <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 52%" }, minWidth: 0 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
            <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 22%" }, minWidth: 0 }}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
        </Box>
    );

    const renderTankView = () => (
        <>
            <Box sx={{ display: "flex", gap: 3, mb: 7, flexDirection: { xs: "column", md: "row" } }}>
                <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 22%" }, minWidth: 0 }}>
                    <Paper sx={{ p: 2, minHeight: 400, height: "100%" }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ textAlign: "center", color: "var(--text-primary)" }}>
                            {t("budgetForm.vesselCalc.inputData")}
                        </Typography>
                        <ExcelResultTable tableType="INPUTS" data={data} />
                    </Paper>
                </Box>
                <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 52%" }, minWidth: 0 }}>
                    <Paper sx={{ p: 2, minHeight: 400, height: "100%" }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ textAlign: "center", color: "var(--text-primary)" }}>
                            {t("budgetForm.vesselCalc.torisphericalDin")}
                        </Typography>
                        <ExcelResultTable tableType="VESSEL" data={data} />
                    </Paper>
                </Box>
                <Box sx={{ flex: { xs: "1 1 auto", md: "0 0 22%" }, minWidth: 0 }}>
                    <Paper sx={{ p: 2, minHeight: 400, height: "100%" }}>
                        <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ textAlign: "center", color: "var(--text-primary)" }}>
                            {t("budgetForm.vesselCalc.tankCapacity")}
                        </Typography>
                        <ExcelResultTable tableType="TANK_CALCULATION" data={data} highlight />
                    </Paper>
                </Box>
            </Box>
        </>
    );

    const renderTowerView = () => (
        <>
            <Paper sx={{ p: 2, minHeight: 400 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2} sx={{ textAlign: "center", color: "var(--text-primary)" }}>
                    {t("budgetForm.vesselCalc.degassingTower")}
                </Typography>
                <ExcelResultTable tableType="TOWER" data={data} />
            </Paper>
        </>
    );

    return (
        <Box sx={{ width: "100%", position: "relative", mb: 7 }}>
            {/* Tabs */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Tabs
                    value={activeView}
                    onChange={(_, v) => setActiveView(v)}
                    textColor="primary"
                    indicatorColor="primary"
                    sx={{
                        "& .MuiTabs-flexContainer": { gap: 1 },
                        "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.9rem",
                            borderRadius: "8px 8px 0 0",
                            color: "var(--text-secondary)",
                            "&.Mui-selected": { color: "primary.main" },
                        },
                        "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 3 },
                    }}
                >
                    <Tab label={t("budgetForm.vesselCalc.tank")} />
                    <Tab label={t("budgetForm.vesselCalc.tower")} />
                </Tabs>
            </Box>

            {error ? (
                <CalculationWarning error={error} />
            ) : data != null ? (
                <Box sx={{ width: "100%" }}>
                    {detailLoading || loading ? renderSkeleton() : (
                        <>
                            {isTower ? renderTowerView() : renderTankView()}
                        </>
                    )}
                </Box>
            ) : (
                <CalculationWarning />
            )}

            <Backdrop
                open={loading}
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: isDarkMode ? "rgba(0, 0, 0, 0.7)" : "rgba(255, 255, 255, 0.85)",
                    color: "var(--color-primary)",
                    zIndex: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <CircularProgress sx={{ color: "primary.main" }} size={40} />
            </Backdrop>
        </Box>
    );
};

export default TorisphericalVesselCalculation;